/**
 * Der Reiter „Historie": jeder scharfe Lauf des Tools, neueste zuerst, und was dabei in
 * Plenty entstand.
 *
 * 🔴 Matze, 30.09.2026: „Welche Artikel haben wir bis jetzt über das Tool angelegt? Wir
 * brauchen auch noch in irgendeiner Form eine Historie." Bis dahin war das nirgends
 * sichtbar — sieben Artikel lagen in der DB, fünf davon waren in Plenty längst gelöscht.
 *
 * Zwei Fragen beantwortet die Seite getrennt:
 *   1. **Was hat Libero gebucht?** — kommt aus der DB, sofort, ohne Plenty.
 *   2. **Steht es noch?** — kommt erst auf Klick („In Plenty prüfen"), Artikel für
 *      Artikel mit Zähler im Knopf. Nur Lese-Calls; das Ergebnis steht in der Spalte
 *      „Plenty heute" und ist mit Datum markiert, weil es beim nächsten Öffnen der
 *      Seite weg ist.
 *
 * Probeläufe fehlen hier mit Absicht: sie schreiben nichts, sie gehören zum Entwurf.
 * Alle Pfade OHNE `/api/`-Prefix — der Core-api-Client hängt ihn selbst an.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw, Search } from 'lucide-react';
import { api } from '@libero/core/services/api';
import LaufButton from '../components/LaufButton';
import CrossSellingPanel from '../components/CrossSellingPanel';
import LaufAktionen from '../components/LaufAktionen';
import Info from '../components/Info';
import { zahl, zeitpunkt } from '../lib/format';
import type { HistorieAntwort, HistorieArtikel, HistorieAttributwert, HistorieLauf, PlentyAbgleich } from '../lib/typen';

const PANEL = 'border border-[#DAD9D9] rounded-lg bg-white overflow-hidden';

const BETRIEBSART: Record<string, string> = {
  neuanlage: 'Neu angelegt',
  bilder: 'Bilder nachgezogen',
  felder: 'Felder aktualisiert',
};

function statusFarbe(status: string): string {
  if (status === 'fertig') return 'bg-green-100 text-green-900';
  if (status === 'laufend') return 'bg-yellow-100 text-yellow-900';
  if (status === 'abgebrochen') return 'bg-yellow-100 text-yellow-900';
  if (status === 'fehler') return 'bg-red-100 text-red-900';
  return 'bg-gray-100 text-gray-700';
}

function standFarbe(stand: PlentyAbgleich['stand']): string {
  if (stand === 'steht') return 'bg-green-100 text-green-900';
  if (stand === 'abweichend') return 'bg-yellow-100 text-yellow-900';
  if (stand === 'geloescht') return 'bg-red-100 text-red-900';
  return 'bg-gray-100 text-gray-700';
}

/** Die Zeile unter dem Lauf: „1 Artikel · 96 Varianten · 8 Bilder", nur was > 0 ist. */
function summeText(l: HistorieLauf): string {
  const s = l.summe;
  const teile: string[] = [];
  if (s.artikel > 0) teile.push(`${zahl(s.artikel)} Artikel`);
  if (s.varianten > 0) teile.push(`${zahl(s.varianten)} Varianten`);
  if (s.bilder > 0) teile.push(`${zahl(s.bilder)} Bilder`);
  if (s.felder > 0) teile.push(`${zahl(s.felder)} Felder`);
  const fehler = s.artikelFehler + s.variantenFehler + s.bilderFehler + s.felderFehler;
  if (fehler > 0) teile.push(`${zahl(fehler)} Fehler`);
  return teile.length > 0 ? teile.join(' · ') : 'nichts geschrieben';
}

/** Kindvarianten, die Libero gebucht hat — Bild-/Feldläufe haben keine Erwartung. */
function erwarteteVarianten(a: HistorieArtikel): number | null {
  return a.status === 'angelegt' ? a.varianten : null;
}

interface Pruefung {
  /** Lauf-Id oder 'alle' */
  ziel: number | 'alle';
  aktuell: number;
  gesamt: number;
}

export default function HistoriePage() {
  const [daten, setDaten] = useState<HistorieAntwort | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<Set<number>>(new Set());
  const [abgleich, setAbgleich] = useState<Map<number, PlentyAbgleich & { am: string }>>(new Map());
  const [pruefung, setPruefung] = useState<Pruefung | null>(null);
  const [pruefFehler, setPruefFehler] = useState<string | null>(null);
  // Reiterwechsel unmountet die Seite — die Prüfschleife darf dann nicht weiter Plenty fragen.
  const aktiv = useRef(true);
  useEffect(() => { aktiv.current = true; return () => { aktiv.current = false; }; }, []);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const antwort = await api.get<{ data: HistorieAntwort }>('/programmes/artikelimport/historie');
      setDaten(antwort.data.data);
      // Den neuesten Lauf gleich aufklappen — meist ist es der, um den es geht.
      const erster = antwort.data.data.laeufe[0]?.id;
      if (erster != null) setOffen(vorher => (vorher.size > 0 ? vorher : new Set([erster])));
    } catch (e: any) {
      const grund = e?.response?.data?.error?.message;
      setFehler(grund ? `Die Historie ließ sich nicht laden: ${grund}` : 'Die Historie ließ sich nicht laden.');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  // Einzelnen Artikel aktivieren (15.10.2026) — dieselbe Regel wie für den ganzen Lauf.
  const [aktiviert, setAktiviert] = useState<Map<string, string>>(new Map());
  const aktiviereArtikel = async (laufId: number, schluessel: string) => {
    if (!window.confirm(`„${schluessel}" jetzt im Shop aktivieren?`)) return;
    const merke = (text: string) => setAktiviert(v => new Map(v).set(`${laufId}|${schluessel}`, text));
    merke('aktiviere …');
    try {
      const r = await api.post<{ data: { aktiviert: number; fehler: number; meldungen: string[] } }>(
        `/programmes/artikelimport/laeufe/${laufId}/aktivieren`, { artikel: [schluessel] },
      );
      merke(r.data.data.fehler > 0 ? `Fehler: ${r.data.data.meldungen.join(' ')}` : `${r.data.data.aktiviert} Varianten aktiv`);
      void pruefen(laufId);
    } catch (e: any) {
      merke(e?.response?.data?.error?.message ?? 'Fehlgeschlagen.');
    }
  };

  const umschalten = (id: number) => {
    setOffen(vorher => {
      const neu = new Set(vorher);
      if (neu.has(id)) neu.delete(id); else neu.add(id);
      return neu;
    });
  };

  /**
   * Artikel für Artikel — ein Aufruf je Item, damit der Zähler im Knopf stimmt und ein
   * einzelner Plenty-Fehler nicht die ganze Prüfung kippt.
   */
  const pruefen = async (ziel: number | 'alle') => {
    if (!daten || pruefung) return;
    const laeufe = ziel === 'alle' ? daten.laeufe : daten.laeufe.filter(l => l.id === ziel);
    const artikel = laeufe.flatMap(l => l.artikel).filter(a => a.itemId != null);
    // Dasselbe Item kann in mehreren Läufen stehen (Neuanlage + Bilder) — einmal reicht.
    const einmalig = new Map<number, HistorieArtikel>();
    for (const a of artikel) {
      const vorhanden = einmalig.get(a.itemId as number);
      if (!vorhanden || (vorhanden.status !== 'angelegt' && a.status === 'angelegt')) einmalig.set(a.itemId as number, a);
    }
    const liste = Array.from(einmalig.values());
    if (liste.length === 0) return;

    setPruefFehler(null);
    setPruefung({ ziel, aktuell: 0, gesamt: liste.length });
    if (ziel !== 'alle') setOffen(vorher => new Set(vorher).add(ziel));
    let fehlgeschlagen = 0;
    let letzterGrund: string | null = null;
    for (let i = 0; i < liste.length; i++) {
      if (!aktiv.current) return;
      const a = liste[i];
      setPruefung({ ziel, aktuell: i + 1, gesamt: liste.length });
      try {
        const antwort = await api.post<{ data: { ergebnisse: PlentyAbgleich[] } }>(
          '/programmes/artikelimport/historie/pruefen',
          { artikel: [{ itemId: a.itemId, varianten: erwarteteVarianten(a) }] },
        );
        const am = new Date().toISOString();
        setAbgleich(vorher => {
          const neu = new Map(vorher);
          for (const e of antwort.data.data.ergebnisse) neu.set(e.itemId, { ...e, am });
          return neu;
        });
      } catch (e: any) {
        fehlgeschlagen++;
        letzterGrund = e?.response?.data?.error?.message ?? (e?.response?.status ? `HTTP ${e.response.status}` : null);
      }
    }
    if (!aktiv.current) return;
    if (fehlgeschlagen > 0) {
      setPruefFehler(
        `${zahl(fehlgeschlagen)} von ${zahl(liste.length)} Artikeln ließen sich nicht prüfen`
        + (letzterGrund ? ` (${letzterGrund}).` : '.'),
      );
    }
    setPruefung(null);
  };

  const laeufe = daten?.laeufe ?? [];
  const artikelGesamt = laeufe.reduce((n, l) => n + l.summe.artikel, 0);
  const pruefbar = new Set(laeufe.flatMap(l => l.artikel).filter(a => a.itemId != null).map(a => a.itemId)).size;
  const letzterAbgleich = Array.from(abgleich.values()).map(a => a.am).sort().pop() ?? null;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Historie</h1>
          <p className="mt-1 text-sm text-[#1A1A1A]/70">
            {laeufe.length === 0
              ? 'Alle Läufe, die etwas in Plenty geschrieben haben.'
              : `${zahl(laeufe.length)} ${laeufe.length === 1 ? 'Lauf' : 'Läufe'} · ${zahl(artikelGesamt)} Artikel angelegt`}
            {daten?.weitere ? ' · ältere Läufe sind nicht geladen' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LaufButton
            laeuft={laedt}
            laufText="Historie wird geladen"
            onClick={() => void laden()}
            variant="outline"
            size="sm"
            icon={<RefreshCw className="mr-2 h-3.5 w-3.5" aria-hidden />}
          >
            Neu laden
          </LaufButton>
          <LaufButton
            laeuft={pruefung?.ziel === 'alle'}
            laufText="Prüft in Plenty"
            schritt={pruefung?.ziel === 'alle' ? `Artikel ${pruefung.aktuell} von ${pruefung.gesamt}` : null}
            prozent={pruefung?.ziel === 'alle' ? Math.round((pruefung.aktuell / pruefung.gesamt) * 100) : null}
            dauerHinweis="etwa 2 Sekunden je Artikel"
            onClick={() => void pruefen('alle')}
            disabled={pruefbar === 0 || pruefung != null}
            size="sm"
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
            icon={<Search className="mr-2 h-3.5 w-3.5" aria-hidden />}
          >
            Alle {zahl(pruefbar)} Artikel in Plenty prüfen
          </LaufButton>
          <Info titel="Was wird geprüft?">
            <p>
              Für jeden Artikel fragt Libero Plenty, ob das Item noch existiert und wie viele
              Variationen es hat. Passt die Zahl zu dem, was Libero gebucht hat, steht „Steht".
              Weicht sie ab, steht „abweichend" — dann wurden Varianten nachträglich gelöscht
              oder hinzugefügt. Es wird nur gelesen, nichts geändert.
            </p>
          </Info>
        </div>
      </div>

      {fehler && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">{fehler}</div>
      )}
      {pruefFehler && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900">{pruefFehler}</div>
      )}
      {letzterAbgleich && (
        <p className="text-xs text-[#1A1A1A]/60">
          Spalte „Plenty heute" zuletzt geprüft {zeitpunkt(letzterAbgleich)} — das Ergebnis gilt nur für diesen Moment.
        </p>
      )}

      {!laedt && laeufe.length === 0 && !fehler && (
        <div className={`${PANEL} p-6 text-sm text-[#1A1A1A]/70`}>
          Noch kein scharfer Lauf. Probeläufe erscheinen hier nicht — sie schreiben nichts.
        </div>
      )}

      <AttributwertListe werte={daten?.attributwerte ?? []} />

      {laeufe.length > 0 && (
        <div className={PANEL}>
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-left text-xs uppercase tracking-wide text-[#1A1A1A]/70">
              <tr>
                <th className="w-8 px-3 py-2" />
                <th className="px-3 py-2">Gestartet</th>
                <th className="px-3 py-2">Was</th>
                <th className="px-3 py-2">Entwurf</th>
                <th className="px-3 py-2">Wer</th>
                <th className="px-3 py-2">Ergebnis</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Plenty</th>
              </tr>
            </thead>
            <tbody>
              {laeufe.map((l) => {
                const istOffen = offen.has(l.id);
                const prueftDiesen = pruefung?.ziel === l.id;
                const pruefbarHier = l.artikel.filter(a => a.itemId != null).length;
                return (
                  <Fragment key={l.id}>
                    <tr
                      className="cursor-pointer border-t border-[#DAD9D9] hover:bg-[#FAFAFA]"
                      onClick={() => umschalten(l.id)}
                    >
                      <td className="px-3 py-2 text-[#1A1A1A]/60">
                        {istOffen ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">{zeitpunkt(l.gestartetAm)}</td>
                      <td className="px-3 py-2">
                        {BETRIEBSART[l.betriebsart] ?? l.betriebsart}
                        {l.profil && <span className="ml-2 text-xs text-[#1A1A1A]/60">{l.profil}</span>}
                      </td>
                      <td className="max-w-xs truncate px-3 py-2 text-[#1A1A1A]/80" title={l.entwurfName}>
                        {l.entwurfName}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">{l.benutzerName ?? '–'}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {summeText(l)}
                        {l.crossSelling?.schreiben && (
                          <span className="ml-2 text-xs text-[#1A1A1A]/60">
                            · {zahl(l.crossSelling.schreiben.gesetzt + l.crossSelling.schreiben.schonDa)} Cross-Selling
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded px-2 py-0.5 text-xs ${statusFarbe(l.status)}`}>{l.status}</span>
                      </td>
                      <td className="px-3 py-2 text-right" onClick={e => e.stopPropagation()}>
                        {pruefbarHier > 0 && (
                          <LaufButton
                            laeuft={prueftDiesen}
                            laufText={`Artikel ${pruefung?.aktuell ?? 0} von ${pruefung?.gesamt ?? pruefbarHier}`}
                            onClick={() => void pruefen(l.id)}
                            disabled={pruefung != null}
                            variant="outline"
                            size="sm"
                          >
                            In Plenty prüfen
                          </LaufButton>
                        )}
                      </td>
                    </tr>
                    {istOffen && (
                      <tr className="border-t border-[#DAD9D9] bg-[#FAFAFA]">
                        <td colSpan={8} className="px-3 py-3">
                          {l.artikel.length === 0 ? (
                            <p className="text-sm text-[#1A1A1A]/60">Dieser Lauf hat nichts geschrieben.</p>
                          ) : (
                            <table className="w-full text-sm">
                              <thead className="text-left text-xs text-[#1A1A1A]/60">
                                <tr>
                                  <th className="px-2 py-1">Artikel</th>
                                  <th className="px-2 py-1">Item-Id</th>
                                  <th className="px-2 py-1">Hauptvariante</th>
                                  <th className="px-2 py-1 text-right">Varianten</th>
                                  <th className="px-2 py-1 text-right">Bilder</th>
                                  {l.betriebsart === 'felder' && <th className="px-2 py-1 text-right">Felder</th>}
                                  <th className="px-2 py-1">Angelegt</th>
                                  <th className="px-2 py-1">Libero</th>
                                  <th className="px-2 py-1">Plenty heute</th>
                                  {l.betriebsart === 'neuanlage' && <th className="px-2 py-1" />}
                                </tr>
                              </thead>
                              <tbody>
                                {l.artikel.map((a) => {
                                  const ab = a.itemId != null ? abgleich.get(a.itemId) : undefined;
                                  return (
                                    <tr key={`${l.id}-${a.schluessel}`} className="border-t border-[#DAD9D9]/60">
                                      <td className="px-2 py-1.5">
                                        <span className="text-[#1A1A1A]">{a.schluessel}</span>
                                        {a.meldung && a.status === 'fehler' && (
                                          <div className="mt-0.5 max-w-xl truncate text-xs text-red-800" title={a.meldung}>{a.meldung}</div>
                                        )}
                                      </td>
                                      <td className="whitespace-nowrap px-2 py-1.5 font-mono text-xs">{a.itemId ?? '–'}</td>
                                      <td className="whitespace-nowrap px-2 py-1.5 font-mono text-xs">
                                        {a.hauptVariationId ?? '–'}
                                        {ab?.nummer && <span className="ml-2 text-[#1A1A1A]/60">{ab.nummer}</span>}
                                      </td>
                                      <td className="whitespace-nowrap px-2 py-1.5 text-right">
                                        {zahl(a.varianten)}
                                        {a.variantenFehler > 0 && <span className="ml-1 text-xs text-red-800">({zahl(a.variantenFehler)} Fehler)</span>}
                                      </td>
                                      <td className="whitespace-nowrap px-2 py-1.5 text-right">
                                        {a.bilder > 0 ? zahl(a.bilder) : '–'}
                                        {a.bilderFehler > 0 && <span className="ml-1 text-xs text-red-800">({zahl(a.bilderFehler)} Fehler)</span>}
                                      </td>
                                      {l.betriebsart === 'felder' && (
                                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                                          {zahl(a.felder)}
                                          {a.felderFehler > 0 && <span className="ml-1 text-xs text-red-800">({zahl(a.felderFehler)} Fehler)</span>}
                                        </td>
                                      )}
                                      <td className="whitespace-nowrap px-2 py-1.5">{zeitpunkt(a.am)}</td>
                                      <td className="px-2 py-1.5">
                                        <span className={`rounded px-2 py-0.5 text-xs ${
                                          a.status === 'angelegt' ? 'bg-green-100 text-green-900'
                                            : a.status === 'geaendert' ? 'bg-blue-100 text-blue-900'
                                              : 'bg-red-100 text-red-900'
                                        }`}>
                                          {a.status}
                                        </span>
                                      </td>
                                      <td className="px-2 py-1.5">
                                        {ab ? (
                                          <span className={`rounded px-2 py-0.5 text-xs ${standFarbe(ab.stand)}`} title={`geprüft ${zeitpunkt(ab.am)}`}>
                                            {ab.text}
                                          </span>
                                        ) : (
                                          <span className="text-xs text-[#1A1A1A]/40">{a.itemId != null ? 'nicht geprüft' : '–'}</span>
                                        )}
                                      </td>
                                      {l.betriebsart === 'neuanlage' && (
                                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                                          {aktiviert.get(`${l.id}|${a.schluessel}`) ? (
                                            <span className="text-xs text-[#1A1A1A]/70">{aktiviert.get(`${l.id}|${a.schluessel}`)}</span>
                                          ) : a.status === 'angelegt' && a.itemId != null && l.status !== 'laufend' ? (
                                            <button type="button" className="text-xs underline" onClick={() => void aktiviereArtikel(l.id, a.schluessel)}>
                                              aktivieren
                                            </button>
                                          ) : null}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                          {l.betriebsart === 'neuanlage' && l.summe.artikel > 0 && l.status !== 'laufend' && (
                            <LaufAktionen lauf={l} aufNeuLaden={() => void laden()} aufPruefen={() => void pruefen(l.id)} />
                          )}
                          {l.betriebsart === 'neuanlage' && l.summe.artikel > 0 && l.status !== 'laufend' && (
                            <CrossSellingPanel laufId={l.id} />
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const ANLASS: Record<string, string> = { knopf: 'Knopf „In Plenty anlegen"', lauf: 'vor dem Schreiblauf', nachgetragen: 'nachgetragen' };

/** In Plenty angelegte Farben und Größen (seit 30.09.2026; die 23 uhlsport-Werte vom 28.09. nachgetragen). */
function AttributwertListe({ werte }: { werte: HistorieAttributwert[] }) {
  const [offen, setOffen] = useState(false);
  if (werte.length === 0) return null;
  const fehler = werte.filter(w => w.status === 'fehler').length;
  return (
    <div className={PANEL}>
      <button type="button" onClick={() => setOffen(o => !o)} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm hover:bg-[#FAFAFA]">
        {offen ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
        <b>Farben & Größen in Plenty angelegt</b>
        <span className="text-[#1A1A1A]/70">{zahl(werte.length - fehler)} Werte{fehler > 0 ? ` · ${zahl(fehler)} fehlgeschlagen` : ''}</span>
      </button>
      {offen && (
        <table className="w-full border-t border-[#DAD9D9] text-sm">
          <thead className="bg-[#F5F5F5] text-left text-xs uppercase tracking-wide text-[#1A1A1A]/70">
            <tr>
              <th className="px-3 py-2">Wann</th><th className="px-3 py-2">Marke</th><th className="px-3 py-2">Attribut</th>
              <th className="px-3 py-2">Wert</th><th className="px-3 py-2">Plenty-Id</th><th className="px-3 py-2">Wie</th>
              <th className="px-3 py-2">Wer</th><th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {werte.map(w => (
              <tr key={w.id} className="border-t border-[#DAD9D9]/60">
                <td className="whitespace-nowrap px-3 py-1.5">{zeitpunkt(w.am)}</td>
                <td className="px-3 py-1.5">{w.profil ?? '–'}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{w.attributId}</td>
                <td className="px-3 py-1.5">{w.wert}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{w.valueId ?? '–'}</td>
                <td className="px-3 py-1.5 text-xs">{ANLASS[w.anlass] ?? w.anlass}{w.entwurfId ? ` · Entwurf ${w.entwurfId}` : ''}</td>
                <td className="px-3 py-1.5">{w.von ?? (w.anlass === 'nachgetragen' ? 'nicht feststellbar' : '–')}</td>
                <td className="px-3 py-1.5">
                  <span className={`rounded px-2 py-0.5 text-xs ${w.status === 'angelegt' ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'}`} title={w.meldung ?? undefined}>{w.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
